import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Autocomplete,
    Typography,
    Alert,
    Box
} from '@mui/material';

const MovimientoKardexDialog = ({
    open,
    onClose,
    onSave,
    filiales = [],
    idFilialDefault = 1,
    articulos = [],
    tiposMovimiento = []
}) => {
    const [idFilial, setIdFilial] = useState(idFilialDefault);
    const [articuloSeleccionado, setArticuloSeleccionado] = useState(null);
    const [idTipoMovimiento, setIdTipoMovimiento] = useState(361); // 361 = Compra
    const [tipoFlujo, setTipoFlujo] = useState('ENTRADA'); // ENTRADA o SALIDA
    const [cantidad, setCantidad] = useState(1);
    const [costoUnitario, setCostoUnitario] = useState(0);
    const [documentoSustento, setDocumentoSustento] = useState('');
    const [detalleGlosa, setDetalleGlosa] = useState('');
    const [requiereAprobacion, setRequiereAprobacion] = useState(false);

    useEffect(() => {
        if (idFilialDefault > 0) {
            setIdFilial(idFilialDefault);
        }
    }, [idFilialDefault, open]);

    useEffect(() => {
        // Actualizar tipo de flujo automáticamente según el tipo de movimiento seleccionado
        const t = tiposMovimiento.find((m) => m.id === idTipoMovimiento);
        if (t) {
            if (t.codigo?.startsWith('ENT_')) {
                setTipoFlujo('ENTRADA');
            } else if (t.codigo?.startsWith('SAL_')) {
                setTipoFlujo('SALIDA');
            }
        }
    }, [idTipoMovimiento, tiposMovimiento]);

    const handleSelectArticulo = (event, val) => {
        setArticuloSeleccionado(val);
        if (val) {
            setCostoUnitario(val.costoUnitarioPromedio || 0);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!articuloSeleccionado) {
            alert('Debe seleccionar un artículo.');
            return;
        }
        if (cantidad <= 0) {
            alert('La cantidad debe ser mayor a 0.');
            return;
        }

        const model = {
            idFilial: Number(idFilial),
            idArticulo: articuloSeleccionado.id,
            idTipoMovimiento: Number(idTipoMovimiento),
            documentoSustento: documentoSustento || 'Sin documento',
            detalleGlosa: detalleGlosa || (tipoFlujo === 'ENTRADA' ? 'Ingreso de material' : 'Salida de material'),
            cantidadEntrada: tipoFlujo === 'ENTRADA' ? Number(cantidad) : 0,
            costoUnitarioEntrada: tipoFlujo === 'ENTRADA' ? Number(costoUnitario) : 0,
            cantidadSalida: tipoFlujo === 'SALIDA' ? Number(cantidad) : 0,
            costoUnitarioSalida: tipoFlujo === 'SALIDA' ? Number(costoUnitario) : 0
        };

        onSave(model, requiereAprobacion);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 'bold', color: '#1f2937' }}>
                Registrar Movimiento de Material (Kardex)
            </DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small" required>
                                <InputLabel>Filial de Destino / Origen</InputLabel>
                                <Select
                                    value={idFilial}
                                    onChange={(e) => setIdFilial(e.target.value)}
                                    label="Filial de Destino / Origen"
                                >
                                    {filiales.filter((f) => f.id > 0).map((f) => (
                                        <MenuItem key={f.id} value={f.id}>
                                            {f.nombre}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small" required>
                                <InputLabel>Tipo de Movimiento</InputLabel>
                                <Select
                                    value={idTipoMovimiento}
                                    onChange={(e) => setIdTipoMovimiento(e.target.value)}
                                    label="Tipo de Movimiento"
                                >
                                    {tiposMovimiento.map((m) => (
                                        <MenuItem key={m.id} value={m.id}>
                                            {m.descripcion}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <Autocomplete
                                options={articulos}
                                getOptionLabel={(option) => `${option.codigo} - ${option.nombre} (Stock: ${option.stockFilial != null ? option.stockFilial : option.stockActual} ${option.unidadMedida})`}
                                value={articuloSeleccionado}
                                onChange={handleSelectArticulo}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Buscar Artículo (Escriba nombre o código de barras)"
                                        size="small"
                                        required
                                        fullWidth
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label={`Cantidad a ${tipoFlujo === 'ENTRADA' ? 'Ingresar' : 'Retirar'}`}
                                type="number"
                                inputProps={{ min: 0.1, step: 'any' }}
                                value={cantidad}
                                onChange={(e) => setCantidad(e.target.value)}
                                required
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Costo Unitario (S/)"
                                type="number"
                                inputProps={{ min: 0, step: '0.01' }}
                                value={costoUnitario}
                                onChange={(e) => setCostoUnitario(e.target.value)}
                                fullWidth
                                size="small"
                                helperText={tipoFlujo === 'SALIDA' ? 'Valorizado al costo promedio' : 'Precio de compra unitario'}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label="Documento de Sustento"
                                value={documentoSustento}
                                onChange={(e) => setDocumentoSustento(e.target.value)}
                                placeholder="Ej: Factura F001-4928, Vale #12, Sesión #42"
                                required
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label="Detalle / Motivo del Movimiento"
                                value={detalleGlosa}
                                onChange={(e) => setDetalleGlosa(e.target.value)}
                                placeholder="Especificar fin terapéutico, área o proveedor..."
                                multiline
                                rows={2}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ p: 1.5, bgcolor: '#f0fdf4', borderRadius: 1, border: '1px solid #bbf7d0' }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={requiereAprobacion}
                                            onChange={(e) => setRequiereAprobacion(e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                Enviar como PENDIENTE para Aprobación
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Si se marca, el movimiento no afectará el stock hasta que un Director o Administrador lo apruebe.
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={onClose} color="inherit">
                        Cancelar
                    </Button>
                    <Button type="submit" variant="contained" color={tipoFlujo === 'ENTRADA' ? 'success' : 'primary'}>
                        {tipoFlujo === 'ENTRADA' ? 'Registrar Ingreso' : 'Registrar Salida'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default MovimientoKardexDialog;
